import { Injectable } from '@angular/core';
import { CurrencyPipe, PercentPipe } from '@angular/common';
import { FormControl } from '@angular/forms';

export enum CALCULATOR_FIELD_TYPES {
    INPUT = 'INPUT',             //  A field that only shows an input
    OUTPUT = 'OUTPUT',           //  A field that only shows a result
    IN_AND_OUT = 'IN_AND_OUT',   //  A field that has an input and output right below it
    OUT_FOOTER = 'OUT_FOOTER'    //  This is a Result that takes a full line. Like `Final Offer Price: $130,000`
}

export enum CALCULATOR_FIELD_UNITS {
    DOLLAR = '$',
    PERCENT = '%'
}

// Note on inputValue and outputValue formats:
// 1. Percentages should be a number decimal format, i.e. like 0.12 (for 12 %)
// 2. Dollar amounts should be a number, i.e. 120000.00 (for $120,000.00)
export interface ICalculatorField {
    id: string; // A slugged value to reference this field by, no spaces
    label: string;
    subLabel: string | null;
    type: CALCULATOR_FIELD_TYPES;
    unit?: CALCULATOR_FIELD_UNITS | null;

    // The cleaned up input value is accessible here for INPUT and IN_AND_OUT types
    // !! Getter Only: To set a value use the field's FormControl
    inputValue?: number | null;

    // Required for all types except INPUT, the result of the calculation should be placed here
    outputValue?: number | null;

    // Required for INPUT and IN_AND_OUT. The entered value after styling has been applied is accessible here
    formControl?: FormControl | null;
    highlight?: boolean;
}

export interface ICalculatorSection {
    fields: ICalculatorField[];
    tabIndexStart?: number; // Each field will have tabIndexStart+i as it's tabindex value
}

@Injectable()
export class CalculatorService {

    constructor(
        private percentPipe: PercentPipe,
        private currencyPipe: CurrencyPipe
    ) {
    }

    /**
     * Sets the input value of the field with the data found in the Parse Object.
     * @param sectionsArray The section arrays that containt the inputs.
     * @param data The Parse object to read the data from.
     */
    setFieldValuesFromParseObject(sectionsArray: ICalculatorSection[][], data: Parse.Object) {
        sectionsArray.forEach(sections => {
            sections.forEach(section => {
                section.fields.forEach(field => {
                    if (field.type === CALCULATOR_FIELD_TYPES.INPUT || field.type === CALCULATOR_FIELD_TYPES.IN_AND_OUT) {
                        const rawValue = data.get(this.convertToCamelCase(field.id));
                        let styledValue;

                        if (field.unit === CALCULATOR_FIELD_UNITS.DOLLAR) {
                            styledValue = this.formatAsUSD(rawValue);
                        } else if (field.unit === CALCULATOR_FIELD_UNITS.PERCENT) {
                            styledValue = this.formatAsPercent(rawValue);
                        } else {
                            styledValue = String(rawValue || '');
                        }

                        if (rawValue === null) {
                            field.inputValue = null;
                            field.formControl?.setValue(null);
                        } else {
                            field.inputValue = Number(rawValue);
                            field.formControl?.setValue(styledValue);
                        }
                    }
                });
            });
        });
    }

    /**
     * Sets the input data of the sections on the given Parse Object.
     * @param dataObject The Parse object to set the data on.
     * @param sectionsArray The section arrays that contains the inputs.
     */
    updateDataObjects(dataObject: Parse.Object, sectionsArray: ICalculatorSection[][]) {
        sectionsArray.forEach(sections => {
            sections.forEach(section => {
                section.fields.forEach(field => {
                    if (field.type === CALCULATOR_FIELD_TYPES.INPUT || field.type === CALCULATOR_FIELD_TYPES.IN_AND_OUT) {
                        dataObject.set(this.convertToCamelCase(field.id), field.inputValue);
                    }
                });
            });
        });
    }

    /**
     * Converts a string seprated by dashes into camelCase.
     * @param input String seperated by dashes.
     */
    convertToCamelCase(input: string): string {
        let workingString = input;
        let prevPos = 0;

        while (workingString.indexOf('-', prevPos + 1) > -1) {
            prevPos = workingString.indexOf('-');
            workingString = this.replaceCharAt(input, prevPos + 1, input[prevPos + 1].toUpperCase());
        }

        return workingString.replace(/-/g, '');

    }

    replaceCharAt(input: string, index: number, replacement: string): string {
        return input.substr(0, index) + replacement + input.substr(index + replacement.length);
    }

    /**
     * Finds a CalculatorField with the given id in a given CalculatorSection.
     * @param sectionArray The CalculatorSection to search in.
     * @param fieldId The id of the field.
     */
    getField(sectionArray: ICalculatorSection[], fieldId: string): ICalculatorField {
        let found: ICalculatorField;
        sectionArray.forEach(section => {
            const result = section.fields.find(field => field.id === fieldId);
            if (result) {
                found = result;
            }
        });
        // @ts-ignore
        return found;
    }

    formatAsPercent(value: number) {
        return this.percentPipe.transform(value, '1.1-2');
    }

    /**
     * Formats the value as USD in format $X.XX
     * @param value The value to format.
     */
    formatAsUSD(value: number) {
        return this.currencyPipe.transform(value, 'USD');
    }

    /**
     * Turns a string like "$130,000.00" into a number.
     */
    dollarStringToNumber(dollarString: string) {
        if (!dollarString) {
            return null;
        }

        return Number(dollarString.replace(/\D/gi, ''));
    }

    /**
     * Turns a string like "26.44%" into a decimal number.
     */
    percentStringToNumber(percentString: string) {
        if (!percentString) {
            return null;
        }
        return Number(percentString.replace(/%/gi, '')) / 100;
    }

    /**
     * Returns true if the inputs is falsy ( false, null, undefined, '' ) but not the numerical value 0
     * @param input Value to check
     */
    isInputUnset(input: any): boolean {
        return !input && (input !== 0);
    }
}
